import { useStore } from 'stimulus-store';
import TomSelect from "tom-select";
import { updateSelect } from "shared/selects/select_helpers";
import { getBuildingsForSelect } from "shared/locales/locales";
import _, { isEmpty } from "lodash";
import { Controller } from "@hotwired/stimulus";
import { buildingStore } from "../../../stores/locales/building_store";
import { DO_NOT_EMIT_EVENTS } from "constants/select_constants";
import { LOCALE_TYPE_REGION, LOCALE_TYPE_BUILDING, LOCALE_TYPE_AREA } from "constants/facilities_constants";

// Connects to data-controller="selects--locales--building-select"
//
// TURBO DRIVE NOTE: buildingStore is a module-level singleton that persists
// across Turbo navigations. This controller clears it in disconnect() when
// this controller's element is torn down (e.g. on Turbo Drive navigation).
// See region_select_controller.js for the full rationale.
export default class extends Controller {
    _region

    static stores = [
        buildingStore
    ]
    static values = {
        parentRegion: String,
        requireArea: Boolean,
        selected: String,
        url: String,
        syncUrl: Boolean,

        // See region_select_controller.js for the full rationale: set only by pages
        // that need organization-wide (not active-project) results, e.g. the Reports
        // filter builder. Left unset, this controller's behavior is unchanged.
        projectScopeToken: String
    }

    connect() {
        useStore(this);
    }

    disconnect() {
        this.resetBuildingStore();
    }

    initialize() {
        this._createBuildingSelect();
    }

    _createBuildingSelect() {
        // Get the building select and the region ID
        const buildingSelectElement = this.element;
        this._region = this.parentRegionValue;
        const buildingId = this.selectedValue;

        // Fall back to URL param for region if not server-rendered (independent hydration)
        const params = new URLSearchParams(window.location.search);
        const regionFromUrl = params.get(LOCALE_TYPE_REGION);
        if (_.isEmpty(this._region) && regionFromUrl) {
            this._region = regionFromUrl;
        }

        // If a region is selected, populate this dropdown with that region's buildings
        if (!_.isEmpty(this._region)) {
            // Fetch this region's buildings
            getBuildingsForSelect(this._region, this.projectScopeTokenValue).then((results) => {
                const control = new TomSelect(buildingSelectElement, {
                    placeholder: 'Select a building',
                    create: false,
                    allowClear: true,
                    valueField: 'id',
                    labelField: 'text',
                    searchField: 'text',
                    sortField: {
                        field: "text",
                        direction: "asc"
                    },
                    onChange: (event) => this._buildingSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                    onDelete: (event) => this._buildingUnselectedEvent(event), // Keep this separate or page loads will be broken
                    maxOptions: null, // tom-select will truncate the select to 50 options by default
                    plugins: ['remove_button'], // This enables a "clear" button on selected items
                    closeAfterSelect: true // This closes the select after a selection is made.
                });

                // Add the expected options to the select. We're doing it this way instead of
                // in the constructor so the items appear in the underlying select. This makes
                // it possible to test using capybara matchers
                updateSelect(this.element.id, results);

                // Syncing will ensure options are stubbed out within the select element. This
                // ensures the tests will have the data in the DOM it can test against
                control.sync();

                // Pre-selection priority: server-rendered selectedValue > URL param > none
                const buildingFromUrl = params.get(LOCALE_TYPE_BUILDING);
                if (!_.isEmpty(buildingId)) {
                    // Server-rendered pre-selection (highest priority)
                    control.setValue(buildingId, DO_NOT_EMIT_EVENTS);
                    const selectedOption = control.options[buildingId];
                    this.setBuildingStoreValue({
                        id: buildingId,
                        name: selectedOption.text
                    });
                } else if (buildingFromUrl) {
                    // URL-parameter pre-selection (silently, no events dispatched)
                    const selectedOption = control.options[buildingFromUrl];
                    if (selectedOption) {
                        control.setValue(buildingFromUrl, DO_NOT_EMIT_EVENTS);
                        this.setBuildingStoreValue({
                            id: buildingFromUrl,
                            name: selectedOption.text
                        });
                    } else {
                        // Invalid building ID in URL — clean up
                        this._removeBuildingIdFromUrl();
                    }
                }
            }).catch(() => {
                // Network/server error fetching buildings — remove building param from URL
                this._removeBuildingIdFromUrl();
            });
        } else {
            // If building param exists without region, clean it up
            const buildingFromUrl = params.get(LOCALE_TYPE_BUILDING);
            if (buildingFromUrl) {
                this._removeBuildingIdFromUrl();
            }

            const control = new TomSelect(buildingSelectElement, {
                placeholder: 'Select a building',
                create: false,
                allowClear: true,
                valueField: 'id',
                labelField: 'text',
                searchField: 'text',
                sortField: {
                    field: "text",
                    direction: "asc"
                },
                onChange: (event) => this._buildingSelectionChanged(event), // Using an arrow function here allows us to reference methods in the class using this
                onDelete: (event) => this._buildingUnselectedEvent(event), // Keep this separate or page loads will be broken
                maxOptions: null, // tom-select will truncate the select to 50 options by default
                plugins: ['remove_button'], // This enables a "clear" button on selected items
                closeAfterSelect: true // This closes the select after a selection is made.
            });

            // Disable the building select since the user hasn't selected a region yet
            this.element.disabled = true;
            if (this.element.tomselect) {
                this.element.tomselect.disable();
            }
        }
    }

    _buildingSelectionChanged(buildingId) {
        // We only want to process events where the user made a selection. Ignore
        // other events. This happens during initialization.
        if (isEmpty(buildingId)) {
            return;
        }

        // Get the selected option text for the building name
        const selectedOption = this.element.tomselect.options[buildingId];
        const buildingName = selectedOption.text

        // Update the store with the building's id and name
        this.setBuildingStoreValue({
            id: buildingId,
            name: buildingName
        });

        /*
         * Send an event telling the area and room select controllers to enable
         * those elements. I stole this method from here:
         * https://lmika.org/2021/02/17/communication-among-stimulus.html
         */
        window.dispatchEvent(new CustomEvent('buildingSelectionMade', {
            detail: {
                region: this._region,
                building: buildingId
            },
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._addBuildingIdToUrl(buildingId);
        }
    }

    _buildingUnselectedEvent(values) {
        // Clear the building store
        this.setBuildingStoreValue({});

        // If we don't have an ID, clear out the current selections and return
        window.dispatchEvent(new Event('buildingSelectionCleared', {
            detail: {},
            bubbles: true // This needs to be explicitly set to enable event bubbling
        }));

        if (this.syncUrlValue) {
            this._removeBuildingIdFromUrl();
        }
    }

    handleRegionChanged(event) {
        // Return if this event is empty. This may happen during initialization.
        if (event === undefined) {
            return;
        }

        // Get the new selected region ID from the event
        this._region = event.detail.region;

        // If this ID is not empty, grab its buildings and update this select
        if (!_.isEmpty(this._region) && !_.isEmpty(event.detail.region)) {
            getBuildingsForSelect(this._region, this.projectScopeTokenValue).then((results) => {
                updateSelect(this.element.id, results);

                // Enable the building select only once its options are actually
                // populated. Enabling synchronously before this resolved meant a
                // fast click (real user or automated test) could open the
                // dropdown while it was still empty.
                this.element.disabled = false;
                if (this.element.tomselect) {
                    this.element.tomselect.enable();
                }
            });
        }
    }

    handleRegionCleared(event) {
        // Clear the variable that stores the region id
        this._region = null;

        // Disable the building select
        this.element.disabled = true;
        if (this.element.tomselect) {
            this.element.tomselect.disable();
        }

        // Clear the buildings
        updateSelect(this.element.id, []);
    }

    _addBuildingIdToUrl(buildingId) {
        const params = new URLSearchParams(window.location.search);
        params.set(LOCALE_TYPE_BUILDING, buildingId);
        this._replaceUrl(params);
    }

    _removeBuildingIdFromUrl() {
        const params = new URLSearchParams(window.location.search);
        params.delete(LOCALE_TYPE_BUILDING);
        params.delete(LOCALE_TYPE_AREA);
        this._replaceUrl(params);
    }

    _replaceUrl(params) {
        const queryString = params.toString();
        const newUrl = queryString
            ? `${window.location.pathname}?${queryString}${window.location.hash}`
            : `${window.location.pathname}${window.location.hash}`;
        history.replaceState(null, '', newUrl);
    }
}
